import { AavoGridColDef } from "src/components/common/dataGrid/types";
import {
	booleanColumn,
	enumColumn,
	floatColumn,
	integerColumn,
	textColumn,
} from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { getPartRevisionLineAcquisitionMethodLabels } from "src/api/generated/erp/db/types/enums/partRevisionLineAcquisitionMethod.ts";
import { calculateSalesMarginPercent, formatLabelWithCurrencyCode } from "src/components/views/erp/sales/salesUtils.ts";
import { PartConfigurationBomLineInspectionDto } from "src/api/generated/erp/configurator/api/partConfigurationBomLineInspectionDto.ts";
import { formatMoneyWithCurrency } from "src/utils/numberUtils.ts";

export function getPartConfigurationBomDataGridColumns({
	vatPercent,
	sellingCurrencyCode,
	domesticCurrencyCode,
	showCostInSellingCurrency,
	useDomesticCostForMargin,
}: {
	vatPercent?: number;
	sellingCurrencyCode?: string | null;
	domesticCurrencyCode?: string | null;
	showCostInSellingCurrency: boolean;
	useDomesticCostForMargin: boolean;
}): AavoGridColDef<PartConfigurationBomLineInspectionDto>[] {
	return [
		integerColumn({
			field: "referenceNo",
			headerName: i18n.t("reference_no"),
			width: 80,
		}),
		{
			field: "part",
			headerName: i18n.t("part"),
			width: 300,
			valueGetter: (_, row) =>
				row.bomLinePartConfigurationId != null ? row.linePartConfigurationDescription : row.linePartLongName,
		},
		floatColumn({
			field: "quantity",
			headerName: i18n.t("quantity"),
			width: 80,
		}),
		textColumn({
			field: "linePartUnitName",
			headerName: i18n.t("unit"),
			width: 80,
		}),
		floatColumn({
			field: "unitPrice",
			headerName: formatLabelWithCurrencyCode(i18n.t("unit_price"), sellingCurrencyCode),
			width: 120,
			renderCell: ({ value }) => moneyCell(value, sellingCurrencyCode),
		}),
		floatColumn({
			field: "totalPrice",
			headerName: formatLabelWithCurrencyCode(i18n.t("total_price_vat_0"), sellingCurrencyCode),
			width: 120,
			renderCell: ({ value }) => moneyCell(value, sellingCurrencyCode),
		}),
		vatPercent != null && {
				field: "totalPriceWithVat",
				headerName: formatLabelWithCurrencyCode(i18n.t("total_price_with_vat"), sellingCurrencyCode),
				type: "number",
				align: "left",
				headerAlign: "left",
				valueGetter: (_, row) => row.totalPrice * (1 + vatPercent / 100),
				renderCell: ({ value }) => moneyCell(value, sellingCurrencyCode),
				width: 120,
			},
		floatColumn({
			field: "bomLineUnitMaterialCost",
			headerName: formatLabelWithCurrencyCode(i18n.t("material_cost"), domesticCurrencyCode),
			width: 120,
			renderCell: ({ value }) => moneyCell(value, domesticCurrencyCode),
		}),
		floatColumn({
			field: "recursiveWorkCost",
			headerName: formatLabelWithCurrencyCode(i18n.t("labour_share_of_material_costs"), domesticCurrencyCode),
			width: 120,
			renderCell: ({ value }) => moneyCell(value, domesticCurrencyCode),
		}),
		floatColumn({
			field: "recursiveTotalWorkingTime",
			headerName: i18n.t("labour_time_from_bom"),
			width: 120,
		}),
		floatColumn({
			field: "bomLineUnitWorkCost",
			headerName: formatLabelWithCurrencyCode(i18n.t("labour_cost"), domesticCurrencyCode),
			width: 120,
			renderCell: ({ value }) => moneyCell(value, domesticCurrencyCode),
		}),
		floatColumn({
			field: "bomLineUnitCost",
			headerName: formatLabelWithCurrencyCode(i18n.t("unit_cost"), domesticCurrencyCode),
			width: 120,
			renderCell: ({ value }) => moneyCell(value, domesticCurrencyCode),
		}),
		floatColumn({
			field: "bomLineTotalCost",
			headerName: formatLabelWithCurrencyCode(i18n.t("total_cost"), domesticCurrencyCode),
			width: 120,
			renderCell: ({ value }) => moneyCell(value, domesticCurrencyCode),
		}),
		showCostInSellingCurrency &&
			floatColumn({
				field: "bomLineTotalCostInSellingCurrency",
				headerName: formatLabelWithCurrencyCode(i18n.t("total_cost"), sellingCurrencyCode),
				width: 160,
				renderCell: ({ value }) => moneyCell(value, sellingCurrencyCode),
			}),
		sellingCurrencyCode != null &&
			floatColumn({
				field: "salesMarginPercent" as any,
				headerName: i18n.t("sales_margin_percent"),
				width: 120,
				valueGetter: (_, row) =>
					calculateBomLineSalesMarginPercent(row, useDomesticCostForMargin),
			}),
		floatColumn({
			field: "bomLineTotalWorkingTime",
			headerName: i18n.t("total_working_time_h"),
			width: 120,
		}),
		enumColumn({
			field: "acquisitionMethod",
			headerName: i18n.t("acquisition_method"),
			enumLabels: getPartRevisionLineAcquisitionMethodLabels(),
		}),
		textColumn({
			field: "bomLineGroupName",
			headerName: i18n.t("bom_line_group"),
			width: 150,
		}),
		textColumn({
			field: "note",
			headerName: i18n.t("note"),
			width: 200,
		}),
		booleanColumn({
			field: "isPhantom",
			headerName: i18n.t("phantom"),
		}),
		textColumn({
			field: "operationLongName",
			headerName: i18n.t("operation"),
			width: 300,
		}),
		integerColumn({
			field: "partConfigurationBomLineId",
			headerName: i18n.t("id"),
		}),
	];
}

function moneyCell(value: number | null | undefined, currencyCode: string | null | undefined): string {
	return value == null ? "" : formatMoneyWithCurrency(value, currencyCode);
}

function calculateBomLineSalesMarginPercent(
	row: PartConfigurationBomLineInspectionDto,
	useDomesticCostForMargin: boolean,
): number | null {
	const cost = useDomesticCostForMargin ? row.bomLineTotalCost : row.bomLineTotalCostInSellingCurrency;
	if (cost == null) return null;

	return calculateSalesMarginPercent({
		cost,
		salesPrice: row.totalPrice,
	});
}
